import { h } from 'preact';
import jsonpatch from 'fast-json-patch';

import Store from 'store/store';
import { joinClasses } from 'utils/utils';
import { confirm, Modal } from 'utils/modal';
import { request } from 'utils/api';
import formatDateTime from 'utils/formatDateTime';
import * as jsonPointer from 'utils/jsonPointer';
import { save } from 'utils/data';
import { getSimplePreviewPageLink, preparePreviewData } from 'utils/preview';
import { isBaseRegion } from 'utils/studioConfig';

import Button from 'components/button/button';
import WithTooltip from 'components/tooltip/withTooltip';

import s from 'components/versionHistory/versionHistory.sss';

// TODO: close on page change
export default class VersionHistory extends Modal {
	s = s;

	title = 'Version history';

	constructor(props) {
		super(props);

		const parsedPath = jsonPointer.parse(props.path);
		this.dataType = parsedPath[0] === 'movies' ? 'movies' : 'sections';
		this.dataId = jsonPointer.get(Store.get().data, props.path)?.id;

		this.state = {
			loading: true,
			rows: [],
			perPage: 10,
			page: 1,
		};
		this.preview = this.preview.bind(this);
		this.onRestoreClick = this.onRestoreClick.bind(this);
		this.goToFirstPage = this.goToFirstPage.bind(this);
		this.goToPrevPage = this.goToPrevPage.bind(this);
		this.goToNextPage = this.goToNextPage.bind(this);
		this.goToLastPage = this.goToLastPage.bind(this);
		this.renderRow = this.renderRow.bind(this);

		if (typeof window !== 'undefined') {
			this.load();
		}
	}

	get maxPage() {
		return Math.ceil(this.state.rows.length / (this.state.perPage || 10));
	}

	async load() {
		this.setState({ error: false });
		try {
			if (!this.dataType || !this.dataId) throw new Error('Invalid data type or ID');
			const data = await request(`/${this.dataType}/history/${this.dataId}`);
			this.data = data.sort((a, b) => b.at.localeCompare(a.at));
			let currentPublished = false;
			const rows = data.reduce((rows, entry) => {
				if (entry.action === 'published') currentPublished = new Date(entry.at || 0);
				if (entry.action !== 'edit') return rows;
				rows.push({
					id: entry.id,
					at: new Date(entry.at || 0),
					by: entry.user?.name,
					published: currentPublished,
					fullData: this.getFullDataAtCommit(entry.id),
				});
				currentPublished = false;
				return rows;
			}, []);
			this.setState({ loading: false, rows });
		} catch (e) {
			console.error(e);
			this.setState({ loading: false, error: true });
			return;
		}
	}

	getFullDataAtCommit(id) {
		const from = this.data.findIndex(entry => entry.id === id);
		if (from === -1) null;
		try {
			const allChanges = this.data.slice(from).map(entry => entry.action === 'edit' && entry.changes).filter(e => e).reverse().flat();
			return jsonpatch.applyPatch({}, structuredClone(allChanges)).newDocument;
		} catch {
			return null;
		}
	}

	preview(e) {
		const row = e.currentTarget.closest('[data-row]');
		if (!row) return;
		const id = row.dataset.row;
		const data = jsonPointer.get(Store.get().data, this.props.path);
		if (!data) return;
		const previewData = { ...data, content: this.getFullDataAtCommit(id) };
		preparePreviewData(previewData);
		window.open(getSimplePreviewPageLink(previewData), 'preview');
	}

	async restore(commitId) {
		try {
			const newData = this.getFullDataAtCommit(commitId);
			if (!newData) throw new Error('Cannot reconstruct data');
			jsonPointer.get(Store.get().data, this.props.path).set('content', newData);
			this.base.close();
			await save(this.props.path);
		} catch {
			// TODO: show error
			this.setState({ restoreError: true });
		}
	}

	async onRestoreClick(e) {
		const row = e.currentTarget.closest('[data-row]');
		if (!row) return;
		const result = await confirm('Are you sure you want to revert the data to this version?\nThis will also override any unsaved changes.');
		if (!result) return;
		this.restore(row.dataset.row);
	}

	goToFirstPage() {
		this.setState({ page: 1 });
	}
	goToPrevPage() {
		this.setState({ page: Math.max(1, (this.state.page || 1) - 1) });
	}
	goToNextPage() {
		this.setState({ page: Math.min(this.maxPage, (this.state.page || 1) + 1) });
	}
	goToLastPage() {
		this.setState({ page: this.maxPage });
	}

	renderRow(row) {
		const fullData = row.fullData;
		const diffWithCurrent = !!fullData && jsonpatch.compare(
			jsonPointer.get(Store.get().data, this.props.path)?.content || {},
			fullData,
		);
		const sameAsCurrent = diffWithCurrent && !diffWithCurrent.length;
		let previewButton = !isBaseRegion() && (
			<Button
				type="button"
				class={s.preview}
				icon="eye"
				disabled={!fullData}
				onClick={this.preview}
				key="preview"
			>
				Preview
			</Button>
		);
		let restoreButton = (
			<Button
				type="button"
				class={s.restore}
				text primary icon="revert"
				disabled={!fullData || sameAsCurrent}
				onClick={this.onRestoreClick}
				key="restore"
			>
				Restore version
			</Button>
		);
		if (!fullData) {
			previewButton = previewButton && <WithTooltip direction="bottom" tooltip="Not available" key="previewTooltip">{previewButton}</WithTooltip>;
			restoreButton = <WithTooltip direction="bottom" tooltip="Not available" key="restoreTooltip">{restoreButton}</WithTooltip>;
		} else if (sameAsCurrent) {
			restoreButton = <WithTooltip direction="bottom" tooltip="Same as the current version" key="restoreTooltip">{restoreButton}</WithTooltip>;
		}
		return (
			<div class={joinClasses(s.row, row.published && s.published)} data-row={row.id} key={row.id}>
				<div class={s.date}>
					<time datetime={row.at.toISOString()}>{formatDateTime(row.at, 'dd MMMM, yyyy "at" hh:mmtt')}</time>
				</div>
				<div class={s.user}>{row.by}</div>
				<div class={s.actions}>
					{previewButton}
					{restoreButton}
				</div>
			</div>
		);
	}

	renderContent() {
		if (this.state.loading) {
			return <div class={s.loader} />;
		}
		const page = this.state.page || 1;
		const perPage = this.state.perPage || 10;
		const from = (page - 1) * perPage;
		const displayedRows = this.state.rows.slice(from, from + perPage);

		const maxPage = this.maxPage;
		const isStart = page <= 1;
		const isEnd = page >= maxPage;
		// TODO: show published date/time in tooltip ?
		return (
			<div class={joinClasses(s.content, s[`type-${this.dataType}`])}>
				<div class={s.versions}>
					{displayedRows.map(this.renderRow)}
				</div>
				<div class={s.pagination}>
					<Button type="button" onClick={this.goToFirstPage} disabled={isStart}>«</Button>
					<Button type="button" onClick={this.goToPrevPage} disabled={isStart}>‹</Button>
					<div class={s.current}>Page {page} of {maxPage}</div>
					<Button type="button" onClick={this.goToNextPage} disabled={isEnd}>›</Button>
					<Button type="button" onClick={this.goToLastPage} disabled={isEnd}>»</Button>
				</div>
			</div>
		);
	}

	renderActions() {}
}